import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, User, Mail, BookOpen, Calendar, UserPlus, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../lib/api';

const AdminCourseStudents = () => {
  const { courseId } = useParams();
  const [students, setStudents] = useState([]);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: '', email: '' });

  const fetchData = async () => {
    try {
      const [studentsRes, courseRes] = await Promise.all([
        api.get(`/admin/courses/${courseId}/students`),
        api.get(`/courses/${courseId}`)
      ]);
      setStudents(studentsRes.data);
      setCourse(courseRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [courseId]);

  const handleAddStudent = async (e) => {
    e.preventDefault();
    if (!newStudent.name || !newStudent.email) {
      toast.error('Completa todos los campos');
      return;
    }
    
    setAddingStudent(true);
    try {
      const res = await api.post(`/admin/courses/${courseId}/enroll-manual`, newStudent);
      const tempPassword = res.data?.temporary_password;
      if (tempPassword) {
        toast.success(`Alumno creado. Contraseña temporal: ${tempPassword}`);
      } else {
        toast.success(`Alumno ${newStudent.name} añadido correctamente`);
      }
      setShowAddModal(false);
      setNewStudent({ name: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir alumno');
    } finally {
      setAddingStudent(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link 
              to="/admin/cursos" 
              className="p-2 rounded-full hover:bg-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-[hsl(var(--text-secondary))]" strokeWidth={1.5} />
            </Link>
            <div>
              <h1 className="text-3xl font-heading text-[hsl(var(--text-primary))]">
                Alumnos inscritos
              </h1>
              {course && (
                <p className="text-[hsl(var(--text-secondary))]">{course.title} • {students.length} alumnos</p>
              )}
            </div>
          </div>
          
          <Button 
            onClick={() => setShowAddModal(true)}
            className="btn-sage"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Añadir alumno
          </Button>
        </div>

        {/* Add Student Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg">Añadir alumno manualmente</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <form onSubmit={handleAddStudent} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre completo</label>
                  <Input
                    value={newStudent.name}
                    onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                    placeholder="María García"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    type="email"
                    value={newStudent.email}
                    onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                    placeholder="maria@ejemplo.com"
                    required
                  />
                </div>
                
                <p className="text-xs text-gray-500">
                  Si el email no existe, se creará una cuenta con contraseña temporal automática.
                </p>
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 btn-sage"
                    disabled={addingStudent}
                  >
                    {addingStudent ? 'Añadiendo...' : 'Añadir alumno'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[hsl(var(--text-secondary))]">Cargando alumnos...</p>
          </div>
        ) : students.length === 0 ? (
          <div className="card-soft text-center p-12">
            <User className="w-12 h-12 text-[hsl(var(--sage))] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[hsl(var(--text-secondary))] mb-4">
              No hay alumnos inscritos en este curso
            </p>
            <Button onClick={() => setShowAddModal(true)} className="btn-sage">
              <UserPlus className="w-4 h-4 mr-2" />
              Añadir primer alumno
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {students.map((enrollment, index) => (
              <div key={enrollment.id} className="card-soft">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[hsl(var(--accent))] font-heading text-lg">
                      {enrollment.user?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[hsl(var(--text-primary))]">
                      {enrollment.user?.name}
                    </p>
                    <p className="text-sm text-[hsl(var(--text-tertiary))] flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {enrollment.user?.email}
                    </p>
                  </div>

                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-[hsl(var(--text-tertiary))]">Progreso</p>
                      <p className="font-medium text-[hsl(var(--sage))]">{enrollment.progress}%</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[hsl(var(--text-tertiary))]">Estado</p>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        enrollment.status === 'completed'
                          ? 'bg-[hsl(var(--sage))]/20 text-[hsl(var(--sage))]'
                          : enrollment.manual_enrollment
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                      }`}>
                        {enrollment.status === 'completed' ? 'Completado' : enrollment.manual_enrollment ? 'Manual' : 'En progreso'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCourseStudents;
